import { IconChevronDown, IconMinus, IconPlus } from "@hawkins/assets";
import { useIntersectionObserver } from "@react-hookz/web";
import { forwardRef, MouseEvent, useEffect, useRef, useState } from "react";
import { Button } from "@components/shared/core/Button";
import MobileSubMenu from "@components/shared/MobileSubMenu";
import cx from "classnames";

// Types
type FilterCategory = { id: string; label: string };

type FAQMobileFiltersProps = {
  filterTitle: string;
  filterCategories: FilterCategory[];
  activeFilters: string[];
  onFilterClick: (filterTag: string) => void;
};

// Filter Button subcomponent
const FilterButton = ({
  category,
  keyPrefix,
  isActive,
  onClick,
}: {
  category: FilterCategory;
  keyPrefix: string;
  isActive: boolean;
  onClick: (filterTag: string) => void;
}) => {
  return (
    <Button
      key={`${keyPrefix}-${category.id}`}
      onClick={(e: MouseEvent) => {
        e.stopPropagation(); // Prevent any parent click events
        onClick(category.id);
      }}
      variant={isActive ? "dark-on-light" : "light-on-blur"}
      size="small"
      aria-selected={isActive}
      className="rounded-full whitespace-nowrap hover:!scale-100"
      onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
      onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
    >
      {category.label}
      {isActive ? (
        <IconMinus size="small" className="w-4 h-4" data-no-roll-animate />
      ) : (
        <IconPlus size="small" className="w-4 h-4" data-no-roll-animate />
      )}
    </Button>
  );
};

export const FAQMobileFilters = forwardRef<
  HTMLDivElement,
  FAQMobileFiltersProps
>(
  (
    { filterTitle, filterCategories, activeFilters, onFilterClick },
    forwardedRef
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTopFilterVisible, setIsTopFilterVisible] = useState(true);
    const topFilterRef = useRef<HTMLDivElement>(null);

    // Use intersection observer to detect when the top filter scrolls out of view
    const topFilterIntersection = useIntersectionObserver(topFilterRef, {
      threshold: [0.1],
      rootMargin: "-10px 0px 0px 0px",
    });

    // Update visibility state when intersection changes
    useEffect(() => {
      if (topFilterIntersection) {
        const isIntersecting = topFilterIntersection.isIntersecting;
        if (isTopFilterVisible !== isIntersecting) {
          setIsTopFilterVisible(isIntersecting);
        }

        // Auto-collapse expanded filter panel when scrolling back to top filter
        if (isIntersecting && isExpanded) {
          setIsExpanded(false);
        }
      }
    }, [topFilterIntersection, isExpanded, isTopFilterVisible]);

    return (
      <>
        {/* Top filter section - initially visible */}
        <div ref={topFilterRef} className="min-h-[60px]">
          <p className="text-brand-cream mb-4 font-medium">{filterTitle}</p>
          <div className="flex flex-wrap gap-2 max-w-full mb-6">
            {filterCategories.map((category) => (
              <FilterButton
                key={`initial-${category.id}`}
                category={category}
                keyPrefix="initial"
                isActive={activeFilters.includes(category.id)}
                onClick={onFilterClick}
              />
            ))}
          </div>
        </div>

        <MobileSubMenu
          title={
            <div
              className="flex justify-between items-center"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {filterTitle}
              <IconChevronDown
                className={cx(
                  "w-5 h-5 ml-2 flex-shrink-0 transition-transform duration-200",
                  isExpanded && "rotate-180"
                )}
              />
            </div>
          }
          ref={forwardedRef}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          hidden={isTopFilterVisible}
        >
          <div className="px-4 pt-4">
            <div className="flex flex-wrap gap-2 max-w-full">
              {filterCategories.map((category) => (
                <FilterButton
                  key={`sticky-${category.id}`}
                  category={category}
                  keyPrefix="sticky"
                  isActive={activeFilters.includes(category.id)}
                  onClick={onFilterClick}
                />
              ))}
            </div>
          </div>
        </MobileSubMenu>
      </>
    );
  }
);
